import { apiClient } from '_app'
import {
  ApiResponse,
  PersonAddressDto,
  PersonDeclarantInformationDto,
  PersonDriverInformationDto,
  PersonDto,
  PersonReportsDto,
  PersonVTS2AccessDto,
  UpdatePersonReportsDto
} from 'dtos'
import { DeclarationOfOwnershipSignerInfoDto } from 'dtos/people'
import { PersonHREmploymentInformationDto } from 'dtos/personHREmploymentInformationDto'
import { PeopleParameters, PersonAddressesParameters } from 'parameters'
import { URIQuery, base64ToArrayBuffer, objectToFormData } from 'utils'

export const getPeople = (parameters: PeopleParameters) =>
  apiClient.get<ApiResponse<PersonDto[]>>('/api/people' + URIQuery({ ...parameters })).then(({ data }) => data)

export const getPersonById = (personId: string) => apiClient.get<ApiResponse<PersonDto>>('/api/people/' + encodeURIComponent(personId)).then(res => res.data)

export const getDeclarantPerson = () => apiClient.get<ApiResponse<PersonDto | undefined>>('/api/people/declarant').then(response => response.data)

export const getDeclarationOfOwnershipSignerPerson = () =>
  apiClient.get<ApiResponse<PersonDto | undefined>>('/api/people/declaration-of-ownership-signer').then(res => res.data)

export const getPersonVTS2Access = (personId: string) =>
  apiClient.get<ApiResponse<PersonVTS2AccessDto>>(`/api/people/${encodeURIComponent(personId)}/vts2-access`).then(response => response.data)

export const updatePersonVTS2Access = (personId: string, values: PersonVTS2AccessDto) =>
  apiClient.put<ApiResponse<PersonVTS2AccessDto>>(`/api/people/${encodeURIComponent(personId)}/vts2-access`, values).then(response => response.data)

export const getPersonReports = (personId: string) =>
  apiClient.get<ApiResponse<PersonReportsDto>>(`/api/people/${encodeURIComponent(personId)}/reports`).then(response => response.data)

export const updatePersonReports = (personId: string, values: UpdatePersonReportsDto) =>
  apiClient.put<ApiResponse<PersonReportsDto>>(`/api/people/${encodeURIComponent(personId)}/reports`, values).then(response => response.data)

export const createPerson = (values: PersonDto) => apiClient.post<ApiResponse<PersonDto>>('/api/people', values).then(res => res.data)

export const updatePerson = (values: PersonDto) => apiClient.put<ApiResponse<PersonDto>>('/api/people', values).then(res => res.data)

export const getPersonAddresses = (personId: string, parameters: PersonAddressesParameters) =>
  apiClient
    .get<ApiResponse<PersonAddressDto[]>>(`/api/people/${encodeURIComponent(personId)}/addresses` + URIQuery({ ...parameters }))
    .then(response => response.data)

export const getDeclarantPersonAddress = () =>
  apiClient.get<ApiResponse<PersonAddressDto | undefined>>('/api/people/addresses/declarant-person-address').then(response => response.data)

export const createPersonAddress = (personId: string, values: PersonAddressDto) =>
  apiClient.post<ApiResponse<PersonAddressDto>>(`/api/people/${encodeURIComponent(personId)}/addresses`, values).then(response => response.data)

export const updatePersonAddress = (personId: string, values: PersonAddressDto) =>
  apiClient.put<ApiResponse<PersonAddressDto>>(`/api/people/${encodeURIComponent(personId)}/addresses`, values).then(response => response.data)

export const getPersonDriverInformation = (personId: string) =>
  apiClient.get<ApiResponse<PersonDriverInformationDto>>(`/api/people/${encodeURIComponent(personId)}/driver-information`).then(response => response.data)

export const updatePersonDriverInformation = (personId: string, values: PersonDriverInformationDto) =>
  apiClient
    .put<ApiResponse<PersonDriverInformationDto>>(`/api/people/${encodeURIComponent(personId)}/driver-information`, values)
    .then(response => response.data)

export const getPersonDeclarantInformation = (personId: string) =>
  apiClient.get<ApiResponse<PersonDeclarantInformationDto>>(`/api/people/${personId}/declarant-information`).then(response => response.data)

export const updatePersonDeclarantInformation = (personId: string, values: PersonDeclarantInformationDto) => {
  const payload = new FormData()

  const { declarantSignatureFile, ...rest } = values

  payload.append('personDeclarantInformationDto', JSON.stringify(rest))
  payload.append('declarantSignatureFile', values.declarantSignatureFile!)

  return apiClient({
    data: payload,
    headers: { 'Content-Type': 'mutlipart/form-data' },
    method: 'put',
    url: `/api/people/${encodeURIComponent(personId)}/declarant-information`
  }).then(response => response.data)
}

export const getDeclarationOfOwnershipSignerInfo = (personGuid: string) =>
  apiClient
    .get<ApiResponse<DeclarationOfOwnershipSignerInfoDto>>(`/api/people/${encodeURIComponent(personGuid)}/declaration-of-ownership-signer-info`)
    .then(res => res.data)

export const editDeclarationOfOwnershipSignerInfo = (personGuid: string, values: DeclarationOfOwnershipSignerInfoDto) =>
  apiClient({
    data: objectToFormData(values),
    headers: { 'Content-Type': 'mutlipart/form-data' },
    method: 'put',
    url: `/api/people/${encodeURIComponent(personGuid)}/declaration-of-ownership-signer-info`
  }).then(res => res.data)

export const getPersonDeclarantSignatureFile = (personId: string, fileName: string) =>
  apiClient.get(`/api/people/${encodeURIComponent(personId)}/declarant-information/${encodeURIComponent(fileName)}`).then(response => {
    const buffer = base64ToArrayBuffer(response.data)
    const file = new Blob([buffer], { type: 'image/png' })
    const fileUrl = window.URL.createObjectURL(file)
    const _newTab = window.open(fileUrl)
  })

export const getPersonHREmploymentInformation = (personId: string) =>
  apiClient
    .get<ApiResponse<PersonHREmploymentInformationDto>>(`/api/people/${encodeURIComponent(personId)}/hr-employment-information`)
    .then(response => response.data)

export const updatePersonHREmploymentInformation = (personId: string, values: PersonHREmploymentInformationDto) =>
  apiClient
    .put<ApiResponse<PersonHREmploymentInformationDto>>(`/api/people/${encodeURIComponent(personId)}/hr-employment-information`, values)
    .then(response => response.data)
